import { base64 } from "@scure/base";
import { Address, OutScript, RawWitness, Transaction, utils } from "@scure/btc-signer";
import { hash160 } from "@scure/btc-signer/utils.js";
import { schnorr, secp256k1 } from "@noble/curves/secp256k1.js";

const encoder = new TextEncoder();

function sameBytes(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
}

function decodeWitness(signature: string): Uint8Array[] {
  const encoded = signature.startsWith("smp") ? signature.slice(3) : signature;
  return RawWitness.decode(base64.decode(encoded));
}

export function buildWalletProofTransaction(message: string, address: string): {
  addressData: ReturnType<ReturnType<typeof Address>["decode"]>;
  script: Uint8Array;
  toSign: Transaction;
} {
  const addressData = Address().decode(address);
  const script = OutScript.encode(addressData);
  const messageHash = schnorr.utils.taggedHash("BIP0322-signed-message", encoder.encode(message));

  const toSpend = new Transaction({ version: 0 });
  toSpend.addOutput({ amount: 0n, script });
  toSpend.addInput({
    txid: new Uint8Array(32),
    index: 0xffffffff,
    sequence: 0,
    finalScriptSig: utils.concatBytes(Uint8Array.of(0x00, 0x20), messageHash),
  });

  const toSign = new Transaction({ version: 0, allowUnknownOutputs: true });
  toSign.addInput({
    txid: toSpend.id,
    index: 0,
    sequence: 0,
    witnessUtxo: { amount: 0n, script },
  });
  toSign.addOutput({ amount: 0n, script: Uint8Array.of(0x6a) });

  return { addressData, script, toSign };
}

export function createWalletOwnershipChallenge(appName: string, address: string): string {
  const nonce = crypto.getRandomValues(new Uint8Array(16));
  const nonceText = Array.from(nonce, (value) => value.toString(16).padStart(2, "0")).join("");
  const runtimeLocation = (globalThis as { location?: { origin?: string } }).location;
  const origin = typeof runtimeLocation?.origin === "string" ? runtimeLocation.origin : "unknown";
  return [
    `${appName} wallet verification`,
    `Origin: ${origin}`,
    `Address: ${address}`,
    `Nonce: ${nonceText}`,
    "This signature is free and does not send Bitcoin.",
  ].join("\n");
}

export function verifyBip322Signature(address: string, message: string, signature: string): boolean {
  try {
    const witness = decodeWitness(signature);
    const { addressData, script, toSign } = buildWalletProofTransaction(message, address);

    if (addressData.type === "wpkh" || addressData.type === "sh") {
      if (witness.length !== 2) return false;
      const [encodedSignature, publicKey] = witness;
      if (!encodedSignature || !publicKey || encodedSignature.length < 2) return false;

      const publicKeyHash = hash160(publicKey);
      if (addressData.type === "wpkh" && !sameBytes(publicKeyHash, addressData.hash)) return false;
      if (addressData.type === "sh") {
        const redeemScript = utils.concatBytes(Uint8Array.of(0x00, 0x14), publicKeyHash);
        if (!sameBytes(hash160(redeemScript), addressData.hash)) return false;
      }

      const hashType = encodedSignature.at(-1);
      if (hashType !== 0x01) return false;
      const scriptCode = utils.concatBytes(Uint8Array.of(0x76, 0xa9, 0x14), publicKeyHash, Uint8Array.of(0x88, 0xac));
      const hashToVerify = toSign.preimageWitnessV0(0, scriptCode, hashType, 0n);
      return secp256k1.verify(encodedSignature.slice(0, -1), hashToVerify, publicKey, { prehash: false, format: "der" });
    }

    if (addressData.type === "tr") {
      if (witness.length !== 1) return false;
      const encodedSignature = witness[0];
      if (!encodedSignature || (encodedSignature.length !== 64 && encodedSignature.length !== 65)) return false;
      const hashType = encodedSignature.length === 64 ? 0x00 : encodedSignature[64];
      if (hashType !== 0x00 && hashType !== 0x01) return false;
      const hashToVerify = toSign.preimageWitnessV1(0, [script], hashType, [0n]);
      return schnorr.verify(encodedSignature.slice(0, 64), hashToVerify, addressData.pubkey);
    }

    return false;
  } catch {
    return false;
  }
}
